/**
 * HEALTH — what is plugged into this till, and how it is holding up.
 *
 * WHERE THE FACTS COME FROM: the device's own `device_report` command
 * (`AGENTIC_POS/src/fleet/executors.ts`), read back through
 * `useDeviceCommands` exactly as `FleetDeviceDrawer` reads it for
 * `DeviceIdentityCard`. Only a `done` command carries a usable `result`, and
 * the result is a free-form `z.record(z.unknown())` on the wire
 * (`FleetCommandSchema.result`), so every read here is optional-chained and
 * falls back to "unknown" / an em dash — never a fabricated zero.
 *
 * 🔴 THE RULE THIS TAB EXISTS TO ENFORCE — two halves, both learned the hard way:
 *
 * 1. THESE TILLS TAKE THIRD-PARTY PRINTERS AND SCANNERS OF ANY MAKE. A
 *    peripheral is identified by its USB CLASS (class 7 = printer; class 3
 *    protocol 1 = HID boot keyboard = wedge scanner), which is what the native
 *    side already did when it stamped `kind` on each entry, and what
 *    `readTillHealth` already did when it built `health.items`. This file
 *    TRUSTS that classification. There is deliberately no vendor-id list here:
 *    a vid/pid allowlist would report a merchant's own working Epson as
 *    "Missing", and `0x0e8d:0x2012` (POS_BASE) is not even unique across our
 *    own hardware.
 *
 * 2. NO REPORT MEANS "UNKNOWN", NEVER "MISSING". Absence of evidence is not
 *    evidence of absence — a red "Missing" against a printer that is printing
 *    receipts right now is the exact bug this design avoids. A till that has
 *    never been asked for a report, or whose report is still queued, reads
 *    Unknown on every row.
 */

import { useMemo, useState } from 'react';
import {
  CreditCard, Printer, ScanLine, Monitor, Hand, RefreshCw, Cable,
} from 'lucide-react';
import type { FleetCommand } from '@platform/shared-types';
import { useDeviceCommands } from '../fleet/useDeviceCommands';
import { useCanManageFleet } from '../../fleet/device/useCanManageFleet';
import DeviceHealthPanel from '../../../components/deviceHealth/DeviceHealthPanel';
import DeviceTimeline from './DeviceTimeline';
import type { DeviceTabProps } from './deviceDetail';

const DASH = '—';

// ---------------------------------------------------------------------------
// Reading the free-form report
// ---------------------------------------------------------------------------

const num = (v: unknown): number | undefined =>
  (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
const bool = (v: unknown): boolean | undefined => (typeof v === 'boolean' ? v : undefined);
const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
const rec = (v: unknown): Record<string, unknown> | undefined =>
  (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined);
const arr = (v: unknown): Record<string, unknown>[] | undefined =>
  (Array.isArray(v) ? v.filter((x): x is Record<string, unknown> => !!x && typeof x === 'object') : undefined);

/** OK / Missing / Unknown — and nothing else. Unknown is a first-class answer. */
type Verdict = 'ok' | 'missing' | 'unknown';

interface PeripheralLine {
  key: string;
  name: string;
  icon: typeof CreditCard;
  verdict: Verdict;
  /** One short line under the name. Absent when there is nothing useful to say. */
  sub?: string;
}

/**
 * A `health.items` entry as `readTillHealth` builds it:
 * `{ key, label, detail, state: 'ready' | 'absent' | 'problem' }`.
 */
function healthItem(result: Record<string, unknown> | null, key: string): Record<string, unknown> | undefined {
  const items = arr(rec(result?.health)?.items);
  return items?.find((i) => str(i.key) === key);
}

function verdictFromState(state: string | undefined): Verdict {
  if (state === 'ready') return 'ok';
  if (state === 'absent' || state === 'problem') return 'missing';
  return 'unknown';
}

/**
 * The USB list, by CLASS-DERIVED `kind` — never by vendor id. `ruledOut` means
 * the native side enumerated it and it never answered, so it does not count.
 */
function usbKind(result: Record<string, unknown> | null, kind: string): Record<string, unknown> | undefined {
  return arr(result?.peripherals)?.find((d) => str(d.kind) === kind && d.ruledOut !== true);
}

/** What the device called this unit. Its product string beats our generic label. */
function usbName(d: Record<string, unknown> | undefined): string | undefined {
  if (!d) return undefined;
  const product = str(d.product);
  return product && product !== '?' ? product : str(d.label);
}

/**
 * One peripheral row's verdict.
 *
 * `health.items` is the primary reading — it is the device saying whether the
 * lane ANSWERED, which is stronger than "something enumerated". The USB list is
 * the fallback for a report that carried peripherals but no health block. With
 * neither, the answer is `unknown`, and `usbListed` decides whether we are
 * entitled to say "missing" at all.
 */
function lane(
  result: Record<string, unknown> | null,
  healthKey: string,
  usbKindName: string,
): { verdict: Verdict; sub?: string } {
  const item = healthItem(result, healthKey);
  if (item) return { verdict: verdictFromState(str(item.state)), sub: str(item.detail) };

  const listed = arr(result?.peripherals);
  if (!listed) return { verdict: 'unknown' };
  const found = usbKind(result, usbKindName);
  return found ? { verdict: 'ok', sub: usbName(found) } : { verdict: 'missing' };
}

/** The five rows the mockup's Health panel shows, in its order. */
export function buildPeripheralLines(result: Record<string, unknown> | null): PeripheralLine[] {
  const terminal = lane(result, 'terminal', 'card_terminal');
  const printer = lane(result, 'printer', 'printer');
  const scanner = lane(result, 'scanner', 'scanner');

  // The customer screen is a DISPLAY, not a USB device — on one board it is a
  // real external panel, on another a virtual display owned by the system, so
  // DisplayManager (`health.display`) and the report's own `customerPanel`
  // flag are the only readings that are right on both.
  const displayItem = healthItem(result, 'display');
  const panelFlag = bool(result?.customerPanel);
  const customer: { verdict: Verdict; sub?: string } = displayItem
    ? { verdict: verdictFromState(str(displayItem.state)), sub: str(displayItem.detail) }
    : panelFlag === undefined
      ? { verdict: 'unknown' }
      : { verdict: panelFlag ? 'ok' : 'missing' };

  // The cashier screen is the one the report was rendered on. A viewport it
  // could measure IS the evidence; nothing else in the payload speaks to it.
  const vp = rec(result?.viewport);
  const w = num(vp?.w);
  const h = num(vp?.h);
  const cashier: { verdict: Verdict; sub?: string } =
    w && h ? { verdict: 'ok', sub: `${w} × ${h}` } : { verdict: 'unknown' };

  return [
    { key: 'terminal', name: 'Card terminal', icon: CreditCard, ...terminal },
    { key: 'printer', name: 'Receipt printer', icon: Printer, ...printer },
    { key: 'scanner', name: 'Barcode scanner', icon: ScanLine, ...scanner },
    { key: 'customer', name: 'Customer screen', icon: Monitor, ...customer },
    { key: 'cashier', name: 'Cashier screen', icon: Hand, ...cashier },
  ];
}

// ---------------------------------------------------------------------------
// Formatting — never invent a number
// ---------------------------------------------------------------------------

function gb(bytes: number | undefined): string | undefined {
  if (bytes === undefined || bytes < 0) return undefined;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

/** Free / total, from whichever key names the report used. */
export function readStorage(result: Record<string, unknown> | null): { free?: number; total?: number } {
  const s = rec(result?.storage);
  return {
    free: num(s?.freeBytes) ?? num(result?.storageFreeBytes) ?? num(result?.freeStorageBytes),
    total: num(s?.totalBytes) ?? num(result?.storageTotalBytes) ?? num(result?.totalStorageBytes),
  };
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

const VERDICT_STYLE: Record<Verdict, { label: string; cls: string }> = {
  ok: { label: 'OK', cls: 'bg-emerald-50 text-emerald-700' },
  missing: { label: 'Missing', cls: 'bg-rose-50 text-rose-700' },
  unknown: { label: 'Unknown', cls: 'bg-stone-100 text-stone-500' },
};

function StatusPill({ verdict }: { verdict: Verdict }) {
  const s = VERDICT_STYLE[verdict];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-bold ${s.cls}`}>
      <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current" />
      {s.label}
    </span>
  );
}

function Pill({ tone, children }: { tone: 'ok' | 'gray'; children: React.ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-bold ${
        tone === 'ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-stone-100 text-stone-500'
      }`}
    >
      <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current" />
      {children}
    </span>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-stone-200 bg-white shadow-sm">
      <div className="px-5 pt-4">
        <h2 className="text-[14.5px] font-bold tracking-tight text-stone-900">{title}</h2>
      </div>
      <div className="px-5 pb-5 pt-4">{children}</div>
    </section>
  );
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-stone-100 py-3 last:border-b-0 last:pb-0">
      <span className="text-[13px] text-stone-500">{label}</span>
      <span className="text-right text-[13px] font-semibold text-stone-900">{children}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The tab
// ---------------------------------------------------------------------------

export default function HealthTab({ row, onRefresh }: DeviceTabProps) {
  const { commands, loading, issue } = useDeviceCommands(row.deviceId, row.deviceType);
  const [reportAsked, setReportAsked] = useState<'idle' | 'asking' | 'done' | 'failed'>('idle');

  // Fleet actions are an EDIT capability. A view-only admin still sees the
  // timeline; they just never get a button that can issue a command.
  // (Collect logs moved to the page header, which owns every capture.)
  const canManageFleet = useCanManageFleet();

  const report = useMemo<FleetCommand | undefined>(
    () => commands.find((c) => c.action === 'device_report'),
    [commands],
  );
  const result = (report?.state === 'done' ? report.result : null) as Record<string, unknown> | null;

  const lines = useMemo(() => buildPeripheralLines(result), [result]);

  // ONE line, not five. The printer and the scanner hang off the same removable
  // USB hub, so they fail together — saying it per row would read as two faults.
  const sharedHub =
    lines.find((l) => l.key === 'printer')?.verdict === 'missing' &&
    lines.find((l) => l.key === 'scanner')?.verdict === 'missing';

  const { free, total } = readStorage(result);
  const storageText =
    free !== undefined && total !== undefined ? `${gb(free)} free of ${gb(total)}`
      : free !== undefined ? `${gb(free)} free`
        : DASH;
  const usedPct =
    free !== undefined && total !== undefined && total > 0
      ? Math.min(100, Math.max(0, Math.round(((total - free) / total) * 100)))
      : null;

  const memory = gb(num(rec(result?.memory)?.totalBytes) ?? num(result?.memoryTotalBytes) ?? num(result?.ramBytes));
  const unsynced = num(result?.unsyncedSaleCount) ?? num(rec(result?.impact)?.unsyncedSaleCount);
  const restart = bool(rec(result?.capabilities)?.restart) ?? bool(result?.canRestart);
  const kiosk = bool(result?.kioskLocked) ?? bool(result?.kioskMode);

  return (
    <div className="space-y-4">
      {row.deviceId ? (
        <>
          <DeviceHealthPanel scope="device" id={row.deviceId} compact />
          <DeviceTimeline deviceId={row.deviceId} />
        </>
      ) : (
        // No `deviceId` means this row has never checked in — there is no
        // live till to scope a `/summary` or `/events` call to. Scoping either
        // call to `undefined` would fire an unscoped device-health request, so
        // neither the panel nor the timeline (nor an action that issues a
        // command to nothing) renders at all.
        <section className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
          <div className="text-[14.5px] font-bold tracking-tight text-stone-900">No health data</div>
          <p className="mt-1 max-w-xl text-[13px] text-stone-500">
            This row has never checked in, so there is no till to ask.
          </p>
        </section>
      )}
      {!loading && !result && (
        <section className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
          <div className="text-[14.5px] font-bold tracking-tight text-stone-900">No device report yet</div>
          <p className="mt-1 max-w-xl text-[13px] text-stone-500">
            {row.deviceId
              ? 'This till has not sent one, so nothing below is known about its hardware. Ask for one — the till answers over its live connection in a few seconds.'
              : 'This row has never checked in, so there is no till to ask.'}
          </p>
          {row.deviceId && canManageFleet && (
            <button
              type="button"
              disabled={reportAsked === 'asking'}
              onClick={() => {
                setReportAsked('asking');
                issue('device_report').then(() => setReportAsked('done')).catch(() => setReportAsked('failed'));
              }}
              className="mr-2 mt-4 inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-[#DC143C] px-4 text-[13.5px] font-semibold text-white hover:bg-[#9F1239] disabled:opacity-60"
            >
              Run device report
            </button>
          )}
          <button
            type="button"
            onClick={onRefresh}
            className="mt-4 inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-stone-200 bg-white px-4 text-[13.5px] font-semibold text-stone-900 hover:bg-stone-50"
          >
            <RefreshCw size={15} aria-hidden="true" />
            Refresh
          </button>
          {reportAsked === 'done' && <p className="mt-2 text-[12px] text-stone-500">Asked — it shows here once the till answers (usually seconds while it is online).</p>}
          {reportAsked === 'failed' && <p role="alert" className="mt-2 text-[12px] text-red-700">Could not ask the till for a report. Try again.</p>}
        </section>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Attached hardware">
          <div>
            {lines.map((l) => {
              const Icon = l.icon;
              return (
                <div
                  key={l.key}
                  className="flex min-h-[44px] items-center gap-3 border-b border-stone-100 py-3 last:border-b-0 last:pb-0"
                >
                  <span className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-stone-100 text-stone-500">
                    <Icon size={16} aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13.5px] font-semibold text-stone-900">{l.name}</span>
                    {l.sub ? <span className="block text-[11.5px] text-stone-400">{l.sub}</span> : null}
                  </span>
                  <StatusPill verdict={l.verdict} />
                </div>
              );
            })}
          </div>

          {sharedHub && (
            <p className="mt-4 flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2.5 text-[12.5px] text-amber-800">
              <Cable size={14} className="mt-0.5 flex-none" aria-hidden="true" />
              Printer and scanner share one removable USB hub — check that cable first.
            </p>
          )}
        </Card>

        <Card title="Condition">
          <div className="pb-1">
            <div className="flex items-center justify-between gap-4">
              <span className="text-[13px] text-stone-500">Storage</span>
              <span className="text-right text-[13px] font-semibold text-stone-900">{storageText}</span>
            </div>
            {usedPct !== null && (
              <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-stone-100">
                <div
                  data-testid="storage-bar"
                  role="progressbar"
                  aria-label="Storage used"
                  aria-valuenow={usedPct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  className="h-full rounded-full bg-emerald-500"
                  style={{ width: `${usedPct}%` }}
                />
              </div>
            )}
          </div>
          <div className="mt-3">
            <KV label="Memory">{memory ?? DASH}</KV>
            <KV label="Unsent sales">
              {unsynced === undefined ? DASH
                : unsynced === 0 ? <span className="text-emerald-700">None</span>
                  : <span className="text-rose-700">{unsynced}</span>}
            </KV>
            <KV label="Remote restart">
              {restart === undefined ? DASH : <Pill tone={restart ? 'ok' : 'gray'}>{restart ? 'Available' : 'Unavailable'}</Pill>}
            </KV>
            <KV label="Kiosk mode">
              {kiosk === undefined ? DASH : <Pill tone={kiosk ? 'ok' : 'gray'}>{kiosk ? 'On' : 'Off'}</Pill>}
            </KV>
          </div>
        </Card>
      </div>
    </div>
  );
}
